import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { ROOMS } from '../../rooms';
import { useWorldStore } from '../../state/worldStore';
import { useCutscene, cutsceneBridge } from '../../city/cutscene';
import { roomBody } from '../../state/roomBody';
import { player } from '../../game/player';
import { useHypeU, elevatorBridge } from '../../city/hypeu';

// THE ELEVATOR HAS YOU (Hype-U, 2026-11-01). Khari: "the camera movement needs
// to be tightened so it doesn't travel through the walls. To clean it up,
// takeover control from the user as soon as they walk into the elevator. then
// position the camera behind them as they make their selection wait for the
// elevator to activate. … When I stepped out of the elevator, my spawn point was
// behind the wall of the classroom and I was unable to enter it."
//
// ONE, in the canvas (PlanetScene, after ChatSceneCamera), so it outlives the
// ride's change of room. Walk into the car and it takes the camera
// (cutsceneBridge: the walk, the clamps and the chase camera stand down; the HUD
// steps out): your body walks to the middle of the car and turns to the doors,
// and the camera settles in the car's back corner behind you, looking out over
// your shoulder while you pick a floor (ElevatorPanel). It holds through the
// ride — doors shut, the car moves, the new floor's doors part — and gives you
// back standing just inside the doors, facing out, so the chase camera starts
// inside the car rather than behind its back wall. "Step out" (the panel) gives
// you back without riding.
//
// Where things are comes from the room's own Elevator (elevatorBridge: its
// room, fit and group), trusted only once it is the room you are in.
const BOARD_S = 0.9;       // walking to the middle and the camera settling
const BACK = 0.4;          // camera: this far behind the car's middle (room units)
const SIDE = 0.14;         // …a little off to the side
const CAM_UP = 0.95;       // …this high over the floor
const EYE = 0.75;          // the body's eye point over its feet (roomBody)
const OUT_STEP = 0.3;      // given back: this far toward the doors from the middle
const ease = (x) => { const t = Math.min(1, Math.max(0, x)); return t * t * (3 - 2 * t); };

export default function ElevatorDirector() {
  const camera = useThree((s) => s.camera);
  const run = useRef(null);
  const v = useMemo(() => ({
    c: new THREE.Vector3(), out: new THREE.Vector3(), side: new THREE.Vector3(), tip: new THREE.Vector3(),
    camPos: new THREE.Vector3(), look: new THREE.Vector3(), body: new THREE.Vector3(),
    m: new THREE.Matrix4(), q: new THREE.Quaternion(), up: new THREE.Vector3(0, 1, 0),
  }), []);

  /** This room's car in the world: v.c (its middle, on the floor), v.out (toward the doors), v.side, unit. */
  const car = () => {
    const ws = useWorldStore.getState();
    const room = ROOMS[ws.interior];
    const el = room?.elevator;
    const space = elevatorBridge.space;
    if (!el || elevatorBridge.roomId !== ws.interior || !space) return null;
    const k = elevatorBridge.k || 1;
    space.updateWorldMatrix(true, false);
    const cx = ((el.car.min[0] + el.car.max[0]) / 2) * k; const cz = ((el.car.min[2] + el.car.max[2]) / 2) * k;
    const r = el.door.rotY || 0;
    v.c.set(cx, 0, cz); space.localToWorld(v.c);
    v.tip.set(cx + Math.sin(r), 0, cz + Math.cos(r)); space.localToWorld(v.tip);
    v.out.subVectors(v.tip, v.c);
    const unit = v.out.length() || 1;
    v.out.y = 0; v.out.normalize();
    v.side.crossVectors(v.out, v.up).normalize();
    return { unit, floorY: v.c.y };
  };

  const release = () => {
    const r = run.current;
    run.current = null;
    if (!r) return;
    const at = car();
    if (at) {
      // Just inside the doors, facing out.
      v.body.copy(v.c).addScaledVector(v.out, OUT_STEP * at.unit);
      if (roomBody.on) {
        roomBody.v.set(v.body.x, at.floorY + EYE * at.unit, v.body.z);
        player.x = v.body.x; player.z = v.body.z; player.y = at.floorY;
      } else {
        camera.position.set(v.body.x, at.floorY + EYE * at.unit, v.body.z);
      }
      v.look.copy(camera.position).addScaledVector(v.out, 2);
      v.m.lookAt(camera.position, v.look, v.up);
      camera.quaternion.setFromRotationMatrix(v.m);
    }
    player.faceYaw = null; player.vx = 0; player.vz = 0;
    if (cutsceneBridge.scene?.kind === 'elevator') { cutsceneBridge.active = false; cutsceneBridge.at = null; cutsceneBridge.scene = null; }
    const cs = useCutscene.getState();
    if (cs.phase === 'cut' && !cs.target) useCutscene.setState({ phase: 'off' });
    useHypeU.setState({ aboard: false });
  };

  useFrame((_, dt) => {
    const ws = useWorldStore.getState();
    const hu = useHypeU.getState();
    const room = ROOMS[ws.interior];
    const want = !!room?.hypeu && !!room?.elevator && (!!hu.ride || (hu.inCar && !hu.arrivedIn));
    let r = run.current;
    if (!r && want) {
      if (cutsceneBridge.active && cutsceneBridge.scene?.kind !== 'elevator') return;   // something else has the camera
      r = run.current = {
        t0: performance.now(), fromPos: camera.position.clone(), fromQuat: camera.quaternion.clone(),
        bodyFrom: roomBody.on ? roomBody.v.clone() : null, room: ws.interior,
      };
      cutsceneBridge.active = true; cutsceneBridge.at = camera.position.clone(); cutsceneBridge.scene = { kind: 'elevator' };
      useCutscene.setState({ phase: 'cut', target: null, caption: null });
      useHypeU.setState({ aboard: true });
    }
    if (!r) return;
    if (!want) { release(); return; }
    const at = car();
    if (!at) return;   // mid-ride, the next room not up yet: hold still (the screen is dimmed)
    // A new room under you (the ride arrived): straight to the framing, no walk.
    if (r.room !== ws.interior) { r.room = ws.interior; r.t0 = performance.now() - BOARD_S * 1000; r.bodyFrom = null; }
    const e = ease((performance.now() - r.t0) / 1000 / BOARD_S);
    const third = roomBody.on;
    const floorY = at.floorY;
    // The body: to the middle of the car, turned to the doors.
    if (third) {
      v.body.set(v.c.x, floorY + EYE * at.unit, v.c.z);
      if (r.bodyFrom && e < 1) {
        const px = roomBody.v.x; const pz = roomBody.v.z;
        roomBody.v.lerpVectors(r.bodyFrom, v.body, e);
        player.vx = dt > 0 ? (roomBody.v.x - px) / dt : 0; player.vz = dt > 0 ? (roomBody.v.z - pz) / dt : 0;
      } else {
        roomBody.v.copy(v.body); player.vx = 0; player.vz = 0;
      }
      player.x = roomBody.v.x; player.z = roomBody.v.z; player.y = floorY;
      player.faceYaw = Math.atan2(v.out.x, v.out.z);
      // The camera: the car's back corner, over the shoulder, looking out.
      v.camPos.copy(v.c).addScaledVector(v.out, -BACK * at.unit).addScaledVector(v.side, SIDE * at.unit);
      v.camPos.y = floorY + CAM_UP * at.unit;
      v.look.copy(v.c).addScaledVector(v.out, 1.6 * at.unit);
      v.look.y = floorY + 0.62 * at.unit;
    } else {
      // First person: your own eyes, in the middle of the car, looking out.
      v.camPos.set(v.c.x, floorY + EYE * at.unit, v.c.z);
      v.look.copy(v.camPos).addScaledVector(v.out, 2 * at.unit);
      v.look.y -= 0.05 * at.unit;
    }
    v.m.lookAt(v.camPos, v.look, v.up);
    v.q.setFromRotationMatrix(v.m);
    if (e < 1) {
      camera.position.lerpVectors(r.fromPos, v.camPos, e);
      camera.quaternion.slerpQuaternions(r.fromQuat, v.q, e);
    } else {
      camera.position.copy(v.camPos);
      camera.quaternion.copy(v.q);
    }
  });

  return null;
}
